import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { fetchSiteReceiver } from '../contacts/site-receiver';
import { shipmentsApi } from '../../api/shipments-api';
import { SearchableSelect, type SearchableSelectOption } from '../../components/SearchableSelect';
import type { Customer, Site } from '../../domain/inventory';
import {
  formatSiteAddress,
  hasSiteAddress,
  isValidPhone,
  PHONE_HINT,
  tidyPhone,
} from '../../domain/siteContact';
import { siteDisplayName, siteLabel, siteSearchAliases } from '../../lib/site-label';
import { PalletEditor } from './PalletEditor';
import { EMPTY_PALLET, palletMeasurements, type PalletDraft } from './pallet-draft';
import {
  CARGONIZER_CARRIERS,
  type ShipmentAddress,
  type ShipmentCreateInput,
  type ShipmentCreateViaCargonizerInput,
  type ShipmentDirection,
  shipsBoxes,
} from '../../domain/shipment';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import { bookingErrorMessage, isUnknownOutcome } from './booking-errors';
import { DirectionChoice } from './DirectionChoice';
import { RebillFreightField } from './RebillFreightField';
import { rebillOptions, rebillReferenceProblem } from './rebill-freight';
import { ReturnShipmentForm } from './ReturnShipmentForm';

interface NewShipmentDrawerProps {
  onClose: () => void;
  onCreated: (shipmentId: string) => void;
  // Set when opened to fulfill a ShipmentRequest -- prefills the
  // destination so it doesn't have to be retyped. Fields stay editable.
  initialDestinationCustomer?: string;
  initialDestinationAddress?: ShipmentAddress;
  // Prefills the destination from this site's address and Receiving
  // contact, once sites have loaded.
  initialSiteId?: string | undefined;
  initialReason?: string;
  // Opens on a customer return, with this Site as the origin.
  initialDirection?: ShipmentDirection | undefined;
}

type Mode = 'cargonizer' | 'manual';

/**
 * Shipment creation. Outbound shipments go from Tidal to a customer; a
 * customer return is collected from a customer Site and delivered to a
 * Tidal location (tidaltech/koi#8085).
 */
export function NewShipmentDrawer(props: NewShipmentDrawerProps) {
  const [direction, setDirection] = useState<ShipmentDirection>(
    props.initialDirection ?? 'OUTBOUND',
  );
  if (direction === 'RETURN') {
    return (
      <ReturnShipmentForm
        onClose={props.onClose}
        onCreated={props.onCreated}
        onDirectionChange={setDirection}
        initialOriginSiteId={props.initialSiteId}
        initialReason={props.initialReason}
      />
    );
  }
  return <OutboundShipmentForm {...props} onDirectionChange={setDirection} />;
}

interface Draft {
  cargonizerConsignmentNumber: string;
  carrier: string;
  destinationSiteId: string;
  destinationCustomer: string;
  line1: string;
  line2: string;
  postcode: string;
  city: string;
  contactName: string;
  contactPhone: string;
  state: 'TRANSFERRED' | 'PENDING';
  reason: string;
  senderReference: string;
}
const EMPTY_DRAFT: Draft = {
  cargonizerConsignmentNumber: '',
  carrier: CARGONIZER_CARRIERS[0],
  destinationSiteId: '',
  destinationCustomer: '',
  line1: '',
  line2: '',
  postcode: '',
  city: '',
  contactName: '',
  contactPhone: '',
  state: 'TRANSFERRED',
  reason: '',
  senderReference: '',
};

function OutboundShipmentForm({
  onClose,
  onCreated,
  onDirectionChange,
  initialDestinationCustomer,
  initialDestinationAddress,
  initialSiteId,
  initialReason,
}: NewShipmentDrawerProps & { onDirectionChange: (direction: ShipmentDirection) => void }) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>('cargonizer');
  const [draft, setDraft] = useState<Draft>(() => ({
    ...EMPTY_DRAFT,
    destinationCustomer: initialDestinationCustomer ?? EMPTY_DRAFT.destinationCustomer,
    line1: initialDestinationAddress?.line1 ?? EMPTY_DRAFT.line1,
    line2: initialDestinationAddress?.line2 ?? EMPTY_DRAFT.line2,
    postcode: initialDestinationAddress?.postcode ?? EMPTY_DRAFT.postcode,
    city: initialDestinationAddress?.city ?? EMPTY_DRAFT.city,
    reason: initialReason ?? EMPTY_DRAFT.reason,
  }));
  const [pallets, setPallets] = useState<PalletDraft[]>([{ ...EMPTY_PALLET }]);
  const [formError, setFormError] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);

  // Cargonizer's own address book ("kunder") -- lets the destination
  // customer field autofill a known site/vendor's address instead of
  // retyping it. Useful in both modes: a manually-recorded shipment still
  // goes to a real address in the book just as often as a cargonizer one.
  const cargonizerCustomers = useQuery({
    queryKey: ['cargonizer-customers'],
    queryFn: () => shipmentsApi.listCargonizerCustomers(),
    staleTime: 5 * 60 * 1000,
  });

  // Sites are the system of record for destination contact/address; the
  // Cargonizer address book above stays as a fallback for destinations that
  // aren't sites (vendors, venues, people).
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => inventoryApi.listSites() });
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
  });
  const customersById = useMemo(
    () => new Map((customers.data ?? []).map((customer) => [customer.id, customer])),
    [customers.data],
  );
  const siteOptions = useMemo<SearchableSelectOption[]>(
    () =>
      (sites.data ?? [])
        .filter((site) => hasSiteAddress(site.address))
        .map((site) => {
          const customer = customersById.get(site.customerId);
          return {
            value: site.id,
            label: `${siteLabel(site)} — ${customer ? customerLabel(customer) : site.customerId}`,
            aliases: [
              ...siteSearchAliases(site),
              formatSiteAddress(site.address) ?? '',
              customer?.name ?? '',
            ].filter(Boolean),
          };
        })
        .sort((left, right) => left.label.localeCompare(right.label)),
    [customersById, sites.data],
  );

  // Fields stay editable after prefill, and edits here never change the
  // site itself -- a one-off correction applies to this shipment only.
  function applySite(siteId: string): void {
    const site = sites.data?.find((candidate) => candidate.id === siteId);
    if (!site) {
      setDraft((current) => ({ ...current, destinationSiteId: '' }));
      return;
    }
    const customer = customersById.get(site.customerId);
    setDraft((current) => ({
      ...current,
      destinationSiteId: site.id,
      destinationCustomer: destinationName(site, customer),
      line1: site.address?.line1 ?? '',
      line2: site.address?.line2 ?? '',
      postcode: site.address?.postcode ?? '',
      city: site.address?.city ?? '',
      contactName: '',
      contactPhone: '',
    }));
    // The site's receiving contact (tidaltech/koi#8359) fills the contact
    // once it loads, unless someone typed one in the meantime.
    void fetchSiteReceiver(site.id).then((receiver) => {
      if (!receiver) return;
      setDraft((current) =>
        current.destinationSiteId !== site.id || current.contactName || current.contactPhone
          ? current
          : { ...current, contactName: receiver.name, contactPhone: receiver.phone ?? '' },
      );
    });
  }

  const appliedInitialSite = useRef(false);
  useEffect(() => {
    if (appliedInitialSite.current || !initialSiteId || !sites.data || !customers.data) return;
    appliedInitialSite.current = true;
    const site = sites.data.find((candidate) => candidate.id === initialSiteId);
    if (site && hasSiteAddress(site.address)) applySite(site.id);
    // applySite only reads the query data checked above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSiteId, sites.data, customers.data]);

  const selectedSite = sites.data?.find((site) => site.id === draft.destinationSiteId);
  // Bring's parcel products ship boxes; the label and form say so.
  const boxes = mode === 'cargonizer' && shipsBoxes(draft.carrier);

  const onSuccess = async (created: { id: string }) => {
    await queryClient.invalidateQueries({ queryKey: ['shipments'] });
    onCreated(created.id);
  };
  const onError = (error: unknown) => {
    setFormError(error instanceof Error ? error.message : 'The shipment could not be created.');
  };

  const createMutation = useMutation({
    mutationFn: (input: ShipmentCreateInput) => shipmentsApi.createShipment(input),
    onSuccess,
    onError,
  });
  const createViaCargonizerMutation = useMutation({
    mutationFn: (input: ShipmentCreateViaCargonizerInput) =>
      shipmentsApi.createShipmentViaCargonizer(input),
    onSuccess,
    onError: (error: unknown) => {
      if (isUnknownOutcome(error)) {
        setUnknownOutcome(true);
        return;
      }
      setFormError(bookingErrorMessage(error));
    },
  });
  const isPending = createMutation.isPending || createViaCargonizerMutation.isPending;

  function update<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  // Fields stay editable after autofill -- a one-off address not yet in
  // Cargonizer's book is still a valid destination, it just has to be typed.
  function applyCargonizerCustomer(name: string): void {
    const match = cargonizerCustomers.data?.find((customer) => customer.name === name);
    if (!match) return;
    setDraft((current) => ({
      ...current,
      destinationSiteId: '',
      destinationCustomer: match.name,
      line1: match.address.line1,
      line2: match.address.line2 ?? '',
      postcode: match.address.postcode,
      city: match.address.city,
      contactName: match.contactPerson ?? '',
      contactPhone: match.phone ?? '',
    }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);
    setUnknownOutcome(false);

    const destinationCustomer = draft.destinationCustomer.trim();
    const line1 = draft.line1.trim();
    const line2 = draft.line2.trim();
    const postcode = draft.postcode.trim();
    const city = draft.city.trim();
    if (!destinationCustomer || !line1 || !postcode || !city) {
      setFormError('Destination customer and address are required.');
      return;
    }
    const destinationAddress = { line1, ...(line2 ? { line2 } : {}), postcode, city };
    const reason = draft.reason.trim() || undefined;

    if (mode === 'cargonizer') {
      const rebillProblem = rebillReferenceProblem(draft.senderReference);
      if (rebillProblem) {
        setFormError(rebillProblem);
        return;
      }
      if (!isValidPhone(draft.contactPhone)) {
        setFormError(`The phone number isn’t valid. ${PHONE_HINT}`);
        return;
      }
      const cargonizerPallets = pallets.map(palletMeasurements);
      createViaCargonizerMutation.mutate({
        carrier: draft.carrier as ShipmentCreateViaCargonizerInput['carrier'],
        destinationCustomer,
        destinationAddress,
        destinationSiteId: selectedSite?.id,
        destinationContactName: draft.contactName.trim() || undefined,
        destinationContactPhone: tidyPhone(draft.contactPhone) || undefined,
        pallets: cargonizerPallets,
        reason,
        senderReference: draft.senderReference.trim(),
      });
      return;
    }

    const cargonizerConsignmentNumber = draft.cargonizerConsignmentNumber.trim();
    const carrier = draft.carrier.trim();
    if (!cargonizerConsignmentNumber || !carrier) {
      setFormError('Consignment number and carrier are required.');
      return;
    }
    const manualPallets = pallets
      .filter((p) => p.cargonizerPieceNumber.trim())
      .map((p) => ({
        id: p.cargonizerPieceNumber.trim(),
        cargonizerPieceNumber: p.cargonizerPieceNumber.trim(),
        ...palletMeasurements(p),
      }));
    createMutation.mutate({
      cargonizerConsignmentNumber,
      carrier,
      destinationCustomer,
      destinationAddress,
      state: draft.state,
      pallets: manualPallets,
      reason,
    });
  }

  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer drawer-wide"
        aria-label="Create new shipment"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="drawer-heading">
          <h2>New shipment</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close new shipment drawer"
          >
            ×
          </button>
        </div>

        <DirectionChoice value="OUTBOUND" onChange={onDirectionChange} disabled={isPending} />

        {formError ? (
          <p role="alert" className="form-error">
            {formError}
          </p>
        ) : null}
        {unknownOutcome ? (
          <PartialFailureNotice
            onCheckCurrentState={() => {
              void queryClient.invalidateQueries({ queryKey: ['shipments'] });
              onClose();
            }}
          />
        ) : null}

        <div className="button-group" role="tablist" aria-label="Shipment creation mode">
          <button
            type="button"
            className={mode === 'cargonizer' ? 'primary' : undefined}
            onClick={() => setMode('cargonizer')}
          >
            Via Cargonizer
          </button>
          <button
            type="button"
            className={mode === 'manual' ? 'primary' : undefined}
            onClick={() => setMode('manual')}
          >
            Enter manually
          </button>
        </div>
        <p className="drawer-version-note">
          {mode === 'cargonizer'
            ? 'koi creates the consignment with Cargonizer directly — no consignment or piece numbers to type.'
            : 'For a shipment already created in Cargonizer’s own UI, or a carrier not wired up yet.'}
        </p>

        {mode === 'manual' ? (
          <label>
            Cargonizer consignment number
            <input
              value={draft.cargonizerConsignmentNumber}
              onChange={(event) => update('cargonizerConsignmentNumber', event.target.value)}
              required
            />
          </label>
        ) : null}

        {mode === 'cargonizer' ? (
          <label>
            Carrier
            <select
              value={draft.carrier}
              onChange={(event) => update('carrier', event.target.value)}
            >
              {CARGONIZER_CARRIERS.map((carrier) => (
                <option key={carrier} value={carrier}>
                  {carrier}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            Carrier
            <input
              value={draft.carrier}
              onChange={(event) => update('carrier', event.target.value)}
              placeholder="e.g. Nordvest Transport"
              required
            />
          </label>
        )}

        <label>
          Destination site (optional)
          <SearchableSelect
            ariaLabel="Destination site"
            value={draft.destinationSiteId}
            onChange={applySite}
            options={[{ value: '', label: 'Not a site' }, ...siteOptions]}
            placeholder={sites.isLoading ? 'Loading sites…' : 'Search sites with an address'}
            disabled={sites.isLoading}
          />
        </label>
        {sites.isError ? (
          <p className="drawer-version-note" role="status">
            Sites could not be loaded; enter the destination below.
          </p>
        ) : null}
        {selectedSite ? (
          <p className="drawer-version-note">
            Prefilled from the site. Changes below apply to this shipment only.
          </p>
        ) : null}

        <label>
          Destination customer
          <input
            value={draft.destinationCustomer}
            onChange={(event) => {
              update('destinationCustomer', event.target.value);
              applyCargonizerCustomer(event.target.value);
            }}
            list="cargonizer-customers-list"
            required
          />
          <datalist id="cargonizer-customers-list">
            {(cargonizerCustomers.data ?? []).map((customer) => (
              <option key={customer.id} value={customer.name} />
            ))}
          </datalist>
        </label>
        {cargonizerCustomers.data?.length ? (
          <p className="drawer-version-note">
            Pick a name from Cargonizer’s address book to autofill its address — fields stay
            editable for a one-off destination.
          </p>
        ) : null}
        <label>
          Street address
          <input value={draft.line1} onChange={(event) => update('line1', event.target.value)} required />
        </label>
        <label>
          Address line 2 (optional)
          <input value={draft.line2} onChange={(event) => update('line2', event.target.value)} />
        </label>
        <div className="form-row">
          <label>
            Postcode
            <input
              value={draft.postcode}
              onChange={(event) => update('postcode', event.target.value)}
              required
            />
          </label>
          <label>
            City
            <input value={draft.city} onChange={(event) => update('city', event.target.value)} required />
          </label>
        </div>

        {mode === 'cargonizer' ? (
          <>
            <div className="form-row">
              <label>
                Contact person (optional)
                <input
                  value={draft.contactName}
                  onChange={(event) => update('contactName', event.target.value)}
                  autoComplete="off"
                />
              </label>
              <label>
                Phone number (optional)
                <input
                  type="tel"
                  value={draft.contactPhone}
                  onChange={(event) => update('contactPhone', event.target.value)}
                  autoComplete="off"
                  aria-invalid={!isValidPhone(draft.contactPhone)}
                />
              </label>
            </div>
            <p className="drawer-version-note">
              Sent to Cargonizer so the carrier can reach the recipient on delivery.
              {!isValidPhone(draft.contactPhone) ? ` ${PHONE_HINT}` : ''}
            </p>
          </>
        ) : null}

        {mode === 'manual' ? (
          <label>
            State
            <select
              value={draft.state}
              onChange={(event) => update('state', event.target.value as Draft['state'])}
            >
              <option value="TRANSFERRED">Transferred to carrier</option>
              <option value="PENDING">Pending</option>
            </select>
          </label>
        ) : null}

        <label>
          Reason (optional)
          <input
            value={draft.reason}
            onChange={(event) => update('reason', event.target.value)}
            placeholder="e.g. replacement camera"
          />
        </label>

        {mode === 'cargonizer' ? (
          <RebillFreightField
            value={draft.senderReference}
            onChange={(value) => update('senderReference', value)}
            options={rebillOptions(sites.data ?? [], selectedSite?.customerId, selectedSite?.id)}
            disabled={isPending}
          />
        ) : null}

        <div className="form-divider">
          <span>{boxes ? 'Boxes' : 'Pallets'}</span>
        </div>

        <PalletEditor
          pallets={pallets}
          onChange={setPallets}
          showPieceNumber={mode === 'manual'}
          boxes={boxes}
        />

        <div className="drawer-actions">
          <button type="button" onClick={onClose} disabled={isPending}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={isPending}>
            {isPending
              ? 'Creating…'
              : mode === 'cargonizer'
                ? 'Create via Cargonizer'
                : 'Create shipment'}
          </button>
        </div>
      </form>
    </div>
  );
}

function customerLabel(customer: Customer): string {
  return customer.displayName?.trim() || customer.name;
}

// Cargonizer consignee name, in the "<Customer> <Site>" form its address
// book already uses (e.g. "Mowi Trommo").
function destinationName(site: Site, customer: Customer | undefined): string {
  const siteName = siteDisplayName(site);
  return customer ? `${customerLabel(customer)} ${siteName}` : siteName;
}
